import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import dayjs from 'dayjs';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { IGetPurchaseInvoice } from 'apis/GraphQL/Variables/purchaseInvoice';
import { PurChaseInvoiceApi } from 'apis/PurchaseInvoiceApis';
import { Button, ButtonFilter, ButtonExport } from 'components';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import {
  IPaginatedPurchaseData,
  IPurchaseInvoiceData,
} from 'interfaces/PurchaseInvoice';
import path from 'routes/Path';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { toastError, toastSuccess } from 'utils/constants/functions';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import formatNumber, {
  formatCurrency,
  formatDate,
} from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import FilterPurchaseInvoice, {
  IPurchaseInvoiceFilterForm,
} from './FilterPurchaseInvoice';
import TablePurchaseInvoice from './TablePurchaseInvoice';

const { PlusOutlined } = icons;

const initFilter = {} as IPurchaseInvoiceFilterForm;
const initData: IPaginatedPurchaseData = {
  items: [] as IPurchaseInvoiceData[],
};

const PurchaseInvoicePage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { setTitle, setBreadcrumb } = useTitle();

  const firstRender = useRef(true);
  const filterRef = useRef<any>(null);

  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [formFilter, setFormFilter] =
    useState<IPurchaseInvoiceFilterForm>(initFilter);

  const [dataTable, setDataTable] = useState<IPaginatedPurchaseData>(initData);
  const [exportLoading, setExportLoading] = useState(false);

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    pageSize = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const defaultFilter = useMemo(() => {
    if (user?.isAdmin || user?.isHO) return {};

    return {
      ...{ plantId: user?.plantCode ?? undefined },
    };
  }, [user]);

  const { mutate: getPurchaseInvoice, isPending: getPurchaseInvoicePending } =
    useMutation({
      mutationFn: (params: IGetPurchaseInvoice) =>
        PurChaseInvoiceApi.getPurchaseInvoice(params),
      onSuccess: (res) => {
        const items: IPurchaseInvoiceData[] =
          res?.data?.getPurchaseOrderList?.items?.map(
            (item: any, index: number) => {
              const parentIndex = index + 1 + pageSize * (page - 1);

              return {
                ...item,
                index: parentIndex,
                children: item?.tpurchaseChilds?.map(
                  (item: any, itemIndex: number) => ({
                    ...item,
                    index: `${parentIndex}.${itemIndex + 1}`,
                  }),
                ),
              };
            },
          );

        setDataTable({
          items,
          pageInfo: res?.data?.getPurchaseOrderList?.pageInfo,
        });
      },
    });

  const refetch = useCallback(
    (values?: IPurchaseInvoiceFilterForm) => {
      const params: IGetPurchaseInvoice = {
        take: pageSize,
        skip: (page - 1) * pageSize,
        ...values,
      };

      getPurchaseInvoice(params);
    },
    [page, pageSize, formFilter],
  );

  useEffect(() => {
    setTitle('Bảng dẻ');
    setBreadcrumb([{ title: 'Kế toán/Thủ quỹ' }, { title: 'Bảng dẻ' }]);
  }, []);

  useEffect(() => {
    if (firstRender.current) return;

    refetch({ ...formFilter, ...defaultFilter });
  }, [refetch, firstRender, formFilter, defaultFilter]);

  useEffect(() => {
    if (!firstRender.current) return;
    firstRender.current = false;

    const searchParams = new URLSearchParams(location.search);
    const params = Object.fromEntries(searchParams);

    const { fromDate, toDate } = params;
    const formattedParams: IPurchaseInvoiceFilterForm = {
      ...params,
      date: [
        fromDate
          ? dayjs(fromDate, 'DD/MM/YYYY')
          : dayjs().subtract(1, 'month').startOf('day'),
        toDate && dayjs(toDate, 'DD/MM/YYYY'),
      ],
    };

    if (filterRef.current)
      filterRef.current.handleSetFormValues({
        ...formattedParams,
        ...defaultFilter,
      });

    setIsOpenFilter(Object.keys(params).length > 2);
    setFormFilter(formattedParams);
  }, [location, firstRender, defaultFilter]);

  const handleFilter = useCallback(
    (values: IPurchaseInvoiceFilterForm) => {
      const { date, ...restValue } = values;

      const queryParams = Object.entries(restValue).reduce(
        (preVal, currentVal) => {
          const [key, value] = currentVal;

          if (value) {
            if (key.includes('toDate') || key.includes('fromDate'))
              preVal[key] = dayjs(value).format('DD/MM/YYYY')?.toString();
            else preVal[key] = value;
          }

          return preVal;
        },
        { page: 1, page_size: pageSize } as Record<string, any>,
      );

      navigate(
        {
          pathname: location.pathname,
          search: queryString.stringify(queryParams, {
            sort: (a, b) => {
              if (a === 'page') return -1;
              if (b === 'page') return 1;
              if (a === 'page_size') return -1;
              if (b === 'page_size') return 1;

              return 0;
            },
          }),
        },
        { replace: true },
      );

      setFormFilter(values);
    },
    [location],
  );

  const handleCancel = useCallback(() => {
    setFormFilter(initFilter);

    navigate(`?page=1&page_size=${pageSize}`);
  }, [pageSize]);

  const handleExportExcel = useCallback(async () => {
    if (!dataTable?.pageInfo?.totalItems) {
      toastError('Không có dữ liệu để xuất');
      return;
    }

    setExportLoading(true);
    try {
      const response = await PurChaseInvoiceApi.getPurchaseInvoice({
        take: dataTable.pageInfo.totalItems,
        skip: 0,
        ...formFilter,
        ...defaultFilter,
      });

      if (response?.data?.getPurchaseOrderList?.items) {
        const exportData = response.data.getPurchaseOrderList.items.map(
          (item: any, index: number) => ({
            ...item,
            index: index + 1,
            // Format các trường tiền tệ
            totalQ10Buy: formatCurrency({
              value: Number(Number(item.totalQ10Buy).toFixed(4)),
              format: 4,
            }),
            totalQ10Acounting: formatCurrency({
              value: Number(Number(item.totalQ10Acounting).toFixed(4)),
              format: 4,
            }),
            finalPrice: formatNumber(item.finalPrice) || '-',
            totalFinalPriceAcounting:
              formatNumber(item.totalFinalPriceAcounting) || '-',
            // Map trạng thái thành tiếng Việt
            purchaseStatus:
              item.purchaseStatus === 'PUR00_02' ? 'Hoàn thành' : 'Mới tạo',
            // Format mã dẻ tách
            purchaseChildsFormatted: item.tpurchaseChilds?.length
              ? item.tpurchaseChilds?.map((child: any) => child?.id)?.join(', ')
              : '-',
            // Format mã bảng kê
            purchaseOrderQ10Formatted: item.tpurchaseOrderQ10?.length
              ? item.tpurchaseOrderQ10?.map((q10: any) => q10?.id)?.join(', ')
              : '-',
            // Format ngày tháng
            createDate: item.createDate
              ? dayjs(item.createDate).format('DD/MM/YYYY HH:MM')
              : '-',
            modifyDate: item.modifyDate
              ? dayjs(item.modifyDate).format('DD/MM/YYYY HH:MM')
              : '-',
          }),
        );

        const columns = [
          { title: 'STT', dataIndex: 'index', width: 50 },
          { title: 'Mã PO', dataIndex: 'poId', width: 150 },
          { title: 'Mã dẻ DMS', dataIndex: 'id', width: 250 },
          {
            title: 'Khách hàng',
            dataIndex: ['tcustomer', 'sapCustomerId'],
            width: 150,
          },
          {
            title: 'Tên khách hàng',
            dataIndex: ['tcustomer', 'storeName'],
            width: 250,
          },
          { title: 'TL Q10', dataIndex: 'totalQ10Buy', width: 100 },
          {
            title: 'TL Q10 sau khi ĐC',
            dataIndex: 'totalQ10Acounting',
            width: 200,
          },
          { title: 'Thành tiền', dataIndex: 'finalPrice', width: 150 },
          {
            title: 'Thành tiền sau khi ĐC',
            dataIndex: 'totalFinalPriceAcounting',
            width: 200,
          },
          { title: 'Trạng thái', dataIndex: 'purchaseStatus', width: 120 },
          {
            title: 'Mã dẻ tách',
            dataIndex: 'purchaseChildsFormatted',
            width: 350,
          },
          {
            title: 'Mã KH cha',
            dataIndex: ['tcustomer', 'mainCustomer', 'id'],
            width: 150,
          },
          { title: 'Đội', dataIndex: 'teamSalesId', width: 250 },
          {
            title: 'Loại thu dẻ',
            dataIndex: ['tpurchType', 'name'],
            width: 180,
          },
          {
            title: 'Mã bảng kê',
            dataIndex: 'purchaseOrderQ10Formatted',
            width: 350,
          },
          { title: 'Mã BTHTT', dataIndex: 'ttId', width: 150 },
          {
            title: 'Thủ kho xác nhận',
            dataIndex: 'warehouseConfirmation',
            width: 200,
          },
          {
            title: 'Thời gian thủ kho xác nhận',
            dataIndex: 'warehouseConfirmationTime',
            width: 220,
          },
          {
            title: 'Kế toán xác nhận',
            dataIndex: 'accountantConfirmation',
            width: 200,
          },
          {
            title: 'Thời gian kế toán xác nhận',
            dataIndex: 'accountantConfirmationTime',
            width: 220,
          },
          { title: 'Chi nhánh', dataIndex: 'plantId', width: 120 },
          {
            title: 'Người tạo',
            dataIndex: ['tcreateUser', 'fullName'],
            width: 180,
          },
          { title: 'Ngày tạo', dataIndex: 'createDate', width: 200 },
          {
            title: 'Người cập nhật',
            dataIndex: ['tmodifyUser', 'fullName'],
            width: 180,
          },
          { title: 'Ngày cập nhật', dataIndex: 'modifyDate', width: 200 },
        ];

        exportExcel_v2(columns, exportData, 'Bảng dẻ');
        toastSuccess('Xuất Excel thành công!');
      }
    } catch (error) {
      toastError('Xuất Excel thất bại!');
    } finally {
      setExportLoading(false);
    }
  }, [dataTable, formFilter, defaultFilter]);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
        </Col>
        <Col>
          <ButtonExport
            loading={exportLoading}
            onClick={handleExportExcel}
            disabled={!dataTable?.pageInfo?.totalItems}
          />
        </Col>
        {(user?.isAccountant || user?.isStorekeeper || user?.isKTD) && (
          <Col>
            <Button
              fill
              title="Tạo"
              iconBefore={<PlusOutlined />}
              handleOnclick={() => navigate(path.ADD_NEW_PURCHASE_INVOICE)}
            />
          </Col>
        )}
      </Row>
      <FilterPurchaseInvoice
        ref={filterRef}
        isOpen={isOpenFilter}
        defaultFilter={defaultFilter}
        onCancel={handleCancel}
        onFinish={handleFilter}
      />
      <TablePurchaseInvoice
        data={dataTable}
        loading={getPurchaseInvoicePending}
      />
    </>
  );
};

export default PurchaseInvoicePage;
